import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconBrandGithub, IconExclamationCircle } from '@tabler/icons-react';
import type { User } from '@xver/@types/user';
import { Endpoints } from '@xver/apis';
import Button from '@xver/components/Atoms/Button/Button';
import ConfirmActionModal from '@xver/components/Atoms/ConfirmActionModal/ConfirmActionModal';
import DialogBox from '@xver/components/Atoms/DialogBox/DialogBox';
import { getErrorMessage } from '@xver/utils/error';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUnlinkGitHubMutation } from '../hooks/useUnlinkGitHubMutation';

interface GitHubConnectionSectionProps {
    user: User;
}

export default function GitHubConnectionSection({ user }: GitHubConnectionSectionProps) {
    const { t } = useTranslation('profile');
    const [confirmOpened, { open: openConfirm, close: closeConfirm }] = useDisclosure(false);
    const [linking, setLinking] = useState(false);
    const unlink = useUnlinkGitHubMutation();

    const handleConnect = async () => {
        setLinking(true);
        try {
            const res = await Endpoints.v1.Authentication.getGithubAuthorizeUrl();
            const authorizeUrl = res?.data?.authorizeUrl;
            if (!authorizeUrl) throw new Error('missing authorize url');
            sessionStorage.setItem('githubOAuthMode', 'link-account');
            window.location.href = authorizeUrl;
        } catch (err) {
            setLinking(false);
            notifications.show({
                title: t('githubConnection.toast.errorTitle'),
                message: getErrorMessage(err),
                icon: <IconExclamationCircle size={18} color="#eb5757" />,
            });
        }
    };

    const handleConfirmUnlink = () => {
        unlink.mutate(undefined, { onSettled: () => closeConfirm() });
    };

    return (
        <DialogBox
            label={t('githubConnection.title')}
            description={t('githubConnection.description')}
            classNames="p-4"
        >
            <div className="w-full flex flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-2 text-sm text-fg/78">
                    <IconBrandGithub size={18} />
                    <span>
                        {user.githubLinked
                            ? t('githubConnection.connected')
                            : t('githubConnection.notConnected')}
                    </span>
                </div>

                {user.githubLinked ? (
                    <Button
                        variant="danger"
                        size="sm"
                        onClick={openConfirm}
                        isLoading={unlink.isPending}
                    >
                        {t('githubConnection.disconnect')}
                    </Button>
                ) : (
                    <Button variant="primary" size="sm" onClick={handleConnect} isLoading={linking}>
                        {t('githubConnection.connect')}
                    </Button>
                )}
            </div>

            <ConfirmActionModal
                opened={confirmOpened}
                onClose={closeConfirm}
                title={t('githubConnection.confirmTitle')}
                message={t('githubConnection.confirmMessage')}
                confirmLabel={t('githubConnection.disconnect')}
                confirmVariant="danger"
                isLoading={unlink.isPending}
                onConfirm={handleConfirmUnlink}
            />
        </DialogBox>
    );
}
